import { updateCigBoxNoteNewEndpoint } from "../api/postCall/updateCigBoxNote";
import { BOOptionModel } from "../data-model/boOptionModel";
import { AllCommentsProps } from "../data-model/busniessOfficeNote";
import { UpdateCigBoxNoteNew } from "../data-model/updateCigBoxNoteNew";
import { ScreenNamesForBigQuery } from "../enums/eventsData";
import { EventsName } from "../enums/eventsName";
import { apiWorkerManager } from "../workerManagers/workerManager";
import { computeGlobalBoValue } from "./busniessOfficeNotesHelper";
import { insertCommentWritebackOncoemrTrailComment } from "./orderTrailComment";
import { getCommentsToWriteFromAllComments } from "./postCommentHelper";
import { validateReportStatusesAgainstBoOptions } from "./validateBoStatus";

/**
 * Parameters required for saving CIG box note changes.
 * @interface SaveCigBoxNoteParams
 * @property {AllCommentsProps[]} allCommentsForUI - Array of comment groups containing notes to write back.
 * @property {string} patientMrn - The patient's Medical Record Number.
 * @property {string} drugName - The name of the drug associated with the order.
 * @property {string} orderId - The unique identifier of the order.
 * @property {string} org_id - The organization ID.
 * @property {string} userEmail - The email of the user performing the action.
 * @property {string} userId - The ID of the user performing the action.
 * @property {string} payerName - The name of the payer/insurance.
 * @property {BOOptionModel[]} boOptions - The BO options from Redux store.
 */
export interface SaveCigBoxNoteParams {
  allCommentsForUI: AllCommentsProps[];
  patientMrn: string;
  drugName: string;
  orderId: string;
  dateOfService: string;
  org_id: string;
  userEmail: string;
  userId: string;
  payerName: string;
  boOptions: BOOptionModel[];
  /** Display name of the provider performing the writeback. */
  providerName: string;
  /** Order BO status before this writeback. */
  previousBoStatus?: string;
}

/**
 * Result object returned after attempting to save CIG box note changes.
 * @interface SaveCigBoxNoteResult
 * @property {boolean} success - Indicates whether the save operation was successful.
 * @property {string} [error] - Error message if the operation failed; undefined on success.
 */
export interface SaveCigBoxNoteResult {
  success: boolean;
  error?: string;
}

/**
 * Saves CIG box note changes by extracting FINAL-type comments, preparing a payload,
 * logging the event to BigQuery, and making an API call to update the notes.
 *
 * @async
 * @param {SaveCigBoxNoteParams} params - Object containing all required parameters for saving changes.
 * @param {AllCommentsProps[]} params.allCommentsForUI - Array of comment groups to process.
 * @param {string} params.patientMrn - The patient's Medical Record Number.
 * @param {string} params.drugName - The drug name associated with the order.
 * @param {string} params.orderId - The unique order identifier.
 * @param {string} params.org_id - The organization ID.
 * @param {string} params.userEmail - Email of the user performing the action.
 * @param {string} params.userId - ID of the user performing the action.
 * @param {string} params.payerName - The payer/insurance name.
 * @returns {Promise<SaveCigBoxNoteResult>} Result object with success status and optional error message.
 * @example
 * const result = await saveCigBoxNoteChanges({
 *   allCommentsForUI: comments,
 *   patientMrn: "12345",
 *   drugName: "DrugA",
 *   orderId: "ORD-001",
 *   org_id: "org-123",
 *   userEmail: "user@example.com",
 *   userId: "user-123",
 *   payerName: "InsuranceCo",
 * });
 * if (result.success) {
 *   console.log("Notes saved successfully");
 * } else {
 *   console.error("Failed to save:", result.error);
 * }
 */
export const saveCigBoxNoteChanges = async (
  params: SaveCigBoxNoteParams,
): Promise<SaveCigBoxNoteResult> => {
  const {
    allCommentsForUI,
    patientMrn,
    drugName,
    orderId,
    dateOfService,
    org_id,
    userEmail,
    userId,
    payerName,
    boOptions,
    providerName,
    previousBoStatus,
  } = params;

  try {
    const allPostComments = getCommentsToWriteFromAllComments(allCommentsForUI);
    const bo_value = computeGlobalBoValue(allCommentsForUI, boOptions);

    const payload: UpdateCigBoxNoteNew = {
      write_back_reports:
        allPostComments && allPostComments.length > 0 ? allPostComments : [],
      patient_id: patientMrn,
      regimen_name: drugName,
      order_id: orderId,
      date_of_service: dateOfService,
      org_id: org_id,
      bo_status: bo_value,
    };

    // Validate that bo_status and all report_status values exist in bo_options
    const reportStatuses = payload.write_back_reports.map(
      (report) => report.report_status,
    );
    const statusesToValidate = [payload.bo_status, ...reportStatuses].filter(
      (status) => status,
    );

    if (statusesToValidate.length > 0) {
      const validationResult = validateReportStatusesAgainstBoOptions(
        boOptions,
        statusesToValidate,
      );

      if (!validationResult.isValid) {
        return {
          success: false,
          error: `Invalid BO status values: ${validationResult.invalidStatuses.join(", ")}`,
        };
      }
    }

    apiWorkerManager.enqueueAndProcess({
      patient_id: patientMrn,
      order_id: orderId,
      org_id: org_id,
      user_id: userId,
      user_email: userEmail,
      event_name: EventsName.WRITE_BACK_NOTES_INITIATED,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.BUSNIESS_OFFICE_SCREEN,
        patient_mrn: patientMrn,
        drug_name: drugName,
        payer_name: payerName,
        bo_value: bo_value,
        comments: JSON.stringify(allCommentsForUI),
      },
    });

    await updateCigBoxNoteNewEndpoint(payload);

    if (allPostComments && allPostComments.length > 0) {
      insertCommentWritebackOncoemrTrailComment({
        orderId,
        orgId: org_id,
        patientId: patientMrn,
        providerEmail: userEmail,
        providerName,
        fromBoStatus: previousBoStatus,
        toBoStatus: bo_value,
        boOptions,
      });
    }

    return {
      success: true,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};
